<template lang="html">
  <section :id="id">
    <base-modal :maxHeight="modalHeight" @on-base-modal-close="cancelModal">
      <!-- header  -->
      <template v-slot:header>
        <div class="m-title">{{ getMessageText(messages.DISP_EL_TABLE) }}</div>
      </template>

      <!-- content  -->
      <element-description
        v-if="isDescriptionReady"
        :label="descriptionText"
        :outer-container-class="'m-items'"
        :elem-description="desc"
        :focus="true"
        @on-element-description-changed="onDeDescriptionChanged"
      ></element-description>

      <div class="m-items">
        <div class="source-label">
          {{ getMessageText(messages.DATA_SOURCE) }}
        </div>

        <search-box-simple
          class="source-list"
          ref="dataSourceList"
          :list="dataSourceList"
          :value="selectedSource"
          @on-search-box-item-selected="onDataSourceSelected"
        />
      </div>
      <div class="m-table_columns">
        <label>{{ getMessageText(messages.TABLE_COLUMNS) }}</label>
        <div ref="listItems" class="m-list_items">
          <draggable-item
            v-for="item in rows"
            :key="item.id"
            :info="item"
            @on-draggable-drop="onDraggableDrop"
            @on-draggable-drag-start="onDraggableStart"
          >
            <template>
              <div class="column-wrapper">
                <column-element
                  :id="item.id"
                  :all-sources="content.dataElements"
                  :selected-source="selectedSource"
                  :column-data="item"
                  :app-type="content.type"
                  @on-delete-column="onDeleteColumn"
                  @on-selected-field-column="onSelectedFieldColumn"
                />
              </div>
            </template>
          </draggable-item>
        </div>

        <div class="m-add_column">
          <button
            class="m-button neutral"
            v-on:click="appendColumn"
            :disabled="selectedSource === null"
          >
            {{ replaceMessageText(messages.ADD_ITEM, messages.COLUMN) }}
          </button>
        </div>
      </div>
      <!-- footer  -->
      <template v-slot:footer>
        <div class="m-group-btns">
          <button class="m-button neutral" v-on:click="cancelModal">
            {{ getMessageText(messages.CANCEL) }}
          </button>
          <button
            class="m-button brand"
            v-on:click="createBtn"
            :disabled="isSubmitDisabled"
          >
            {{ getMessageText(messages.OK) }}
          </button>
        </div>
      </template>

      <div ref="deleteColumnModal">
        <simple-modal
          ref="simpleModal"
          :show="displaySimpleModal"
          :message="getMessageText(messages.ARE_YOU_SURE)"
          :cancel-text="getMessageText(messages.CANCEL)"
          :ok-text="getMessageText(messages.OK)"
          @on-simple-modal-cancel="onCancelDeleteColumn"
          @on-simple-modal-ok="onConfirmDeleteColumn"
        />
      </div>
    </base-modal>
  </section>
</template>

<script lang="js">
  import { v4 as uuidv4 } from "uuid";
  import store from "@/store";
  import expressionBuilder from "@/views/drag-drop-pages/components/utils/expression-builder";
  import BaseModal from "@/views/drag-drop-pages/components/modal/BaseModal";
  import SearchBoxSimple from "@/components/SearchBoxSimple";
  import DraggableItem from "@/views/drag-drop-pages/components/list/DraggableItem";
  import ColumnElement from "@/views/drag-drop-pages/components/builder/modal/ColumnElement";
  import SimpleModal from "@/views/drag-drop-pages/components/modal/SimpleModal";
  import sortBy from "lodash/sortBy";
  import dragAndDrop from "@/views/drag-drop-pages/components/utils/dragAndDrop";
  import ElementDescription from "@/components/ElementDescription";
  import {NAVIGATION_ACTIONS} from "@/static/global-variables";

  export default  {
    name: "add-table-modal",
    components: {
      BaseModal,
      SearchBoxSimple,
      DraggableItem,
      ColumnElement,
      SimpleModal,
      ElementDescription
    },
    props: {
      id: {
        type: String
      },
      editData: {
        type: Object
      }
    },
    computed: {
      messages() {
        return store.state.messages;
      },
      isSubmitDisabled() {
        return this.selectedSource === null || this.selectedSource === undefined || this.isNotValidFieldList;
      },
      desc() {
        return this.editData ? this.editData.props.description : "";
      },
      descriptionText() {
        return this.getMessageText(this.messages.DESCRIPTION);
      }
    },
    mounted () {
      const appId = dragAndDrop.getAppId();
      this.isDescriptionReady = true;
      this.content = JSON.parse(JSON.stringify(expressionBuilder.getAppContent(appId)));
      if(!this.content) {
        console.error(`No app content found for appId ${appId}`);
        return;
      }
      // filterGlobalSources() will also append fields if dataElement is of type listDataElements
      this.dataSourceList = expressionBuilder.filterGlobalSources(this.content.dataElements);
      if(this.editData) {
        this.setupEditModalData(this.editData);
      }
    },
    data () {
      return {
        content: {},
        dataSourceList: [],
        selectedSource: null,
        displaySimpleModal: false,
        modalHeight: 500,
        rows: [],
        isNotValidFieldList: true,
        elementDescription: null,
        isDescriptionReady: false
      }
    },
    methods: {
      setupEditModalData(editData) {
        // get selectedSource from this.dataSourceList to make
        // sure we have fields if selectedSource is of type listDataElements
        this.onDeDescriptionChanged(editData.props.description)
        this.selectedSource = this.dataSourceList.find(e => e.id === editData.props.dataSource.id);
        for(const el of editData.props.columns) {
          this.addColumn(el);
        }
        this.validateFields();
      },
      onDataSourceSelected(ctx) {
        if (!ctx || ctx.isEmpty) {
          this.selectedSource = null;
        } else {
          this.selectedSource = ctx;
        }
      },
      reset() {
        this.rows = [];
      },
      cancelModal() {
        this.isDescriptionReady = false;
        dragAndDrop.makeAllComponentsDraggable();
        this.$emit("on-add-modal-close", {id: this.id});
      },
      createBtn() {
        const obj = {
          modal: {
            id: this.id,
            create: "TableComponent",
            editElementId: this.editData? this.editData.id: null
          },
          props: {
            description: this.elementDescription,
            columns: [],
            dataSource: this.selectedSource
          }
        };
        for(const row of this.rows) {
          delete row.isLocaleAwareFormatting;
          obj.props.columns.push(row);
        }
        dragAndDrop.makeAllComponentsDraggable();
        this.$emit("on-add-modal-done", obj);
      },
      appendColumn() {
        this.addColumn();
      },
      addColumn(el) {
        const id = "column-" + uuidv4();
        el = el? el : {};
        el.id = id;
        el.rowId = this.rows.length;
        this.rows.push(el);
        this.validateFields();
        setTimeout(() => {
          if (this.$refs.listItems.lastElementChild.getElementsByClassName("m-input")[0]) {
            this.$refs.listItems.lastElementChild.getElementsByClassName("m-input")[0].focus()
          }
        }, 20)
      },
      onDraggableStart(ev) {
        this.from = ev.data.info;
      },
      onCancelDeleteColumn() {
        this.displaySimpleModal = false;
      },
      onConfirmDeleteColumn() {
        const filtered = this.rows.filter(item => item.id !== this.deleteItem.id);
        this.rows = filtered;
        this.displaySimpleModal = false;
        this.validateFields();
      },
      onSelectedFieldColumn(obj) {
        const row = this.rows.find(e => e.id === obj.id);
        row.headerExpression = obj.headerExpression;
        row.field = obj.field;
        row.isLocaleAwareFormatting = obj.isLocaleAwareFormatting;
        row.lineWrap = obj.lineWrap;
        row.userSortable = obj.userSortable
        this.validateFields();
      },
      validateFields() {
        this.isNotValidFieldList = !this.rows.length || this.rows.some(e => this.isNotValidField(e));
      },
      isNotValidField(obj) {
        let notValid = !obj.headerExpression || !obj.field;
        if(!notValid) {
          notValid = obj.field.dataType.toLowerCase() === "date" && !obj.isLocaleAwareFormatting && !obj.field.format;
          // Add validation for dataActions array when ready
          if(!notValid && obj.field.action && obj.field.action.navigation) {
            const nav = obj.field.action.navigation;
            if(nav.type === NAVIGATION_ACTIONS.VIEW_RECORD || nav.type === NAVIGATION_ACTIONS.EDIT_RECORD) {
              notValid = !nav.type || !nav.recordId;
            } else {
              notValid = !nav.type || !nav.target || nav.target.some(e => e.Studio_Id_vod__c === undefined);
            }
          }
        }
        return notValid;
      },
      onDeleteColumn(obj) {
        this.deleteItem = obj;
        this.displaySimpleModal = true;
      },
      onDraggableDrop(ev) {
        const from = this.from;
        const to = ev.data.info;
        if(from.rowId !== to.rowId) {
          if(to.rowId < from.rowId) {
            this.moveDown(from, to);
          } else {
            this.moveUp(from, to);
          }
          const rows = sortBy(this.rows, "rowId", "asc");
          this.rows = [];
          this.$nextTick(() => {
            this.rows = rows;
          });

        }
      },
      moveDown(from, to) {
        let tmpRowId = 0;
        for(const row of this.rows) {
          if(row.id === to.id) {
            from.rowId = tmpRowId++;
            row.rowId = tmpRowId++;
          } else if(row.id !== from.id) {
            row.rowId = tmpRowId++;
          }
        }
      },
      moveUp(from, to) {
        const length = this.rows.length;
        let tmpRowId = length - 1;
        for(let i = length - 1; i > -1; i--) {
          const row = this.rows[i];
          if(row.id === to.id) {
            from.rowId = tmpRowId--;
            row.rowId = tmpRowId--;
          } else if(row.id !== from.id) {
            row.rowId = tmpRowId--;
          }
        }
      },
      onDeDescriptionChanged(input) {
        this.elementDescription = input;
      }
    }
  }
</script>

<style scoped lang="scss">
  @import "@/styles/components/main";
  @include drag-drop-a-tag;
  .m-items {
    display: flex;
    flex-direction: column;
    margin-bottom: 10px;
    .source-label {
      flex-basis: 1.75rem;
    }
    .source-list {
      flex-basis: 100%;
    }
  }

  .m-title {
    text-align: left;
  }
  .m-table_columns {
    display: flex;
    flex-direction: column;
    .m-list_items {
    }
    label {
      margin-bottom: 4px;
    }
  }
  .m-add_column {
    margin-top: 10px;
  }
  .column-wrapper {
    margin-bottom: 0.5rem;
  }
</style>
